<!-- // plans result page -->
<script setup lang="ts">
import BButton from '@/components/BButton.vue';
import { toastController } from '@ionic/vue';
import BwellFeedbackModal from '@/views/BwellFeedbackModal.vue';
import {
  IonContent,
  IonLoading,
  IonSpinner,
  IonPage,
  useIonRouter,
  loadingController,
  modalController,
  IonFab,
  IonFabButton,
  IonIcon,
  IonCard,
} from '@ionic/vue';
import BSlideHeader from '@/components/BSlideHeader.vue';
import Globe from '@/components/illustrations/Globe.vue';
import BPlanItem from '@/components/BPlanItem.vue';
import { provide, ref, onMounted, computed, nextTick, watch } from 'vue';
import { useStore } from '@/stores/main';
import { useBackendFetch } from '@/fetch';
import Star from '@/components/icons/Star.vue';
import monsterUrl from '/assets/monster.png';
import SpeechBubble from '@/components/SpeechBubble.vue';
import { arrowDown, checkmarkCircle, trophy, mail, close } from 'ionicons/icons';

const router = useIonRouter();
const store = useStore();
const loading = ref(false);
const plan = ref<any>(null);
const contentRef = ref<InstanceType<typeof IonContent> | null>(null);
const goals = computed(() => (plan.value as any)?.goals || []);
const scrollButtonVisible = ref(false);
const animationComplete = ref(false);

// Screenshot and email functionality
let html2canvasDyn: typeof import('html2canvas') | null = null;
const exportInProgress = ref(false);
const showEmailModal = ref(false);
const mentorEmail = ref('');
const emailSending = ref(false);
const pageRef = ref<HTMLElement | null>(null);
const confettiActive = ref(false);

// Email limit tracking - max 3 emails per livsstilsplan
const MAX_EMAILS_PER_PLAN = 3;
const emailsSentCount = ref(0);
const emailLimitReached = computed(() => emailsSentCount.value >= MAX_EMAILS_PER_PLAN);
const emailsRemaining = computed(() => MAX_EMAILS_PER_PLAN - emailsSentCount.value);

// Get email count from localStorage based on profile ID and hostname (to prevent local/prod conflicts)
function getEmailCountKey() {
  const profileId = store.profile?.id || store.profile?.user_id || 'unknown';
  const hostname = window.location.hostname || 'unknown';
  return `mentor_email_count_${hostname}_${profileId}`;
}

function loadEmailCount() {
  try {
    const key = getEmailCountKey();
    const stored = localStorage.getItem(key);
    emailsSentCount.value = stored ? parseInt(stored, 10) : 0;
  } catch (e) {
    emailsSentCount.value = 0;
  }
}

function incrementEmailCount() {
  emailsSentCount.value++;
  try {
    const key = getEmailCountKey();
    localStorage.setItem(key, emailsSentCount.value.toString());
  } catch (e) {
    console.error('Failed to save email count:', e);
  }
}

// Watch modal state for debugging
watch(showEmailModal, (newVal) => {
  console.log('showEmailModal changed to:', newVal);
});

onMounted(async () => {
  console.log('PlanResultsPage mounted, showEmailModal:', showEmailModal.value);
  loadEmailCount(); // Load email count from localStorage
  loading.value = true;
  try {
    console.log('🔍 Starting plan results page...');
    console.log('🔍 Store profile:', store.profile);
    
    if (!store.profile?.id) {
      console.error('❌ No profile ID found!');
      loading.value = false;
      return;
    }
    
    console.log('🔍 Fetching plan data...');
    
    // Prefer complete plan to ensure action plans and factors are present
    plan.value = await store.fetchCompletePlan();
    if (!plan.value || !plan.value.goals || plan.value.goals.length === 0) {
      plan.value = await store.fetchPlan();
    }
    // Retry once if still empty
    if (!plan.value || !plan.value.goals || plan.value.goals.length === 0) {
      await new Promise(r => setTimeout(r, 250));
      plan.value = await store.fetchCompletePlan();
    }
    console.log('📊 Plan data from backend:', plan.value);
    // Build base label map from goals to enrich factor labels
    const baseGoals = await store.fetchGoals().catch(() => [] as any[]);
    const baseLabelMap = new Map<number, string>();
    (Array.isArray(baseGoals) ? baseGoals : []).forEach((g: any) => {
      const id = Number(g?.category_id);
      if (id) baseLabelMap.set(id, g?.label || '');
    });
    // Also fetch factor summary to further enrich labels
    const summary = await store.fetchFactorsSummary().catch(() => null as any);
    if (summary) {
      const list: any[] = Array.isArray(summary) ? summary : (Array.isArray(summary?.factors) ? summary.factors : []);
      list.forEach((item: any) => {
        const id = Number(item?.category_id);
        if (!id) return;
        const lbl = item?.label || item?.name || '';
        if (lbl && !baseLabelMap.has(id)) baseLabelMap.set(id, lbl);
      });
    }
    // Normalize goals - use ONLY backend data for consistency across browsers
    // Do NOT fall back to localStorage as it causes inconsistency in private/different browsers
    if (plan.value?.goals && Array.isArray(plan.value.goals)) {
      plan.value.goals = plan.value.goals.map((g: any) => {
        const id = Number(g?.category_id);
        
        // Use factors from backend only - no localStorage fallback
        let factors: any[] = Array.isArray(g.factors) ? g.factors : [];
        
        // Enrich factor labels from baseLabelMap (fetched from API, not localStorage)
        if (factors && factors.length) {
          factors = factors.map((f: any) => {
            const fid = Number(f?.category_id);
            const baseLabel = baseLabelMap.get(fid) || '';
            let label = f.label || baseLabel || '';
            const norm = (label || '').toString().trim().toLowerCase();
            if (!label || norm === 'risk' || norm.startsWith('risk ')) {
              label = baseLabel || label || '';
            }
            // Use values from backend response
            const value = f.value != null ? Number(f.value) : 1;
            const vision = f.vision != null ? Number(f.vision) : 5;
            return { ...f, category_id: fid || f.category_id, label, value, vision };
          });
          g.factors = factors;
        }
        return g;
      });
    }
    
    if (!plan.value) {
      console.error('❌ No plan data received from backend!');
      loading.value = false;
      return;
    }
    
    console.log('📊 Raw plan data (JSON):', JSON.stringify(plan.value, null, 2));
    
    // Backend is now fixed - sambandsfaktorer should be included in the main plan response
    console.log('✅ Backend fixed - sambandsfaktorer should be included in plan response');
    
    // Debug each goal's data structure - backend should now provide complete data
    if (plan.value?.goals) {
      plan.value.goals.forEach((goal: any, index: number) => {
        console.log(`🔍 Goal ${index} (${goal.label}) from fixed backend:`, {
          action_plan: goal.action_plan,
          factors: goal.factors,
          factors_length: goal.factors?.length || 0,
          date: goal.date,
          target_date: goal.target_date
        });
        
        // Check if backend is providing the factors correctly
        if (goal.factors && Array.isArray(goal.factors) && goal.factors.length > 0) {
          console.log(`✅ Goal ${index} has sambandsfaktorer from backend:`, goal.factors);
          goal.factors.forEach((factor: any, fIndex: number) => {
            console.log(`   Factor ${fIndex}:`, {
              category_id: factor.category_id,
              label: factor.label,
              value: factor.value,
              vision: factor.vision
            });
          });
        } else {
          console.log(`⚠️ Goal ${index} has no sambandsfaktorer - either none were selected or backend issue`);
          console.log(`   Raw factors data:`, goal.factors);
          console.log(`   Is array?`, Array.isArray(goal.factors));
          console.log(`   Length:`, goal.factors?.length);
        }
      });
    }
    
    console.log('📊 Final plan data:', plan.value);
    console.log('📊 Goals count:', plan.value?.goals?.length || 0);
    
    // Debug each goal individually
    if (plan.value?.goals) {
      plan.value.goals.forEach((goal: any, index: number) => {
        console.log(`📊 Goal ${index}:`, {
          label: goal.label,
          category_id: goal.category_id,
          value: goal.value,
          vision: goal.vision,
          target_date: goal.target_date,
          action_plan: goal.action_plan,
          factors: goal.factors
        });
      });
    }
    
    // Start animation sequence after data is loaded
    setTimeout(() => {
      animationComplete.value = true;
      confettiActive.value = true;
      
      // Hide confetti after 4 seconds
      setTimeout(() => {
        confettiActive.value = false;
      }, 4000);
    }, 500);
    
  } catch (error) {
    console.error('❌ Failed to fetch plan:', error);
  } finally {
    loading.value = false;
  }

  // Check content height after loading
  nextTick(() => {
    checkContentHeight();
  });
  
  // Add scroll event listener to hide button when at bottom
  if (contentRef.value) {
    contentRef.value.$el.addEventListener('ionScroll', handleScroll);
  }
  
  // Check for small screens
  checkScreenSize();
  window.addEventListener('resize', checkScreenSize);
});

function checkScreenSize() {
  scrollButtonVisible.value = window.innerWidth <= 768;
}

function handleScroll(event: any) {
  const { scrollTop, scrollHeight, clientHeight } = event.target;
  // Hide button when near bottom (within 100px)
  scrollButtonVisible.value = scrollTop < (scrollHeight - clientHeight - 100);
}

function checkContentHeight() {
  if (contentRef.value) {
    const content = contentRef.value.$el;
    const scrollHeight = content.scrollHeight;
    const clientHeight = content.clientHeight;
    
    // Only show scroll button if content exceeds viewport
    if (scrollHeight > clientHeight + 50) {
      scrollButtonVisible.value = true;
    } else {
      scrollButtonVisible.value = false;
    }
  }
}

async function openOneLastThingModal() {
  const loadingQuestions = await loadingController.create({
    message: 'Vänta...',
    cssClass: 'custom-loading',
  });
  await loadingQuestions.present();
  
  try {
    await store.fetchFeedbackQuestions();
    await loadingQuestions.dismiss();

    if (store.feedbackQuestions && store.feedbackQuestions.length) {
      const modal = await modalController.create({
        component: BwellFeedbackModal,
        backdropDismiss: false,
        cssClass: 'feedback-modal',
      });
      await modal.present();
      const { data } = await modal.onWillDismiss();
      
      const loading = await loadingController.create({
        message: 'Avslutar...',
        cssClass: 'custom-loading',
      });
      await loading.present();
      
      // Check feedback submission result
      if (data?.success || data?.alreadySubmitted) {
        console.log('Feedback submission successful or already submitted, finishing plan');
        await store.finishPlan();
      } else {
        console.error('Feedback submission failed:', data?.error);
        const toast = await toastController.create({
          message: 'Failed to save feedback. Please try again.',
          duration: 3000,
          position: 'bottom',
          color: 'danger',
        });
        await toast.present();
      }
      
      await loading.dismiss();
    } else {
      const loading = await loadingController.create({
        message: 'Avslutar...',
        cssClass: 'custom-loading',
      });
      await loading.present();
      await store.finishPlan();
      await loading.dismiss();
    }
    
    // Navigate home with animation
    router.replace('/home');
    
  } catch (error) {
    console.error('Error during feedback process:', error);
    await loadingQuestions.dismiss();
    const toast = await toastController.create({
      message: 'An error occurred. Please try again.',
      duration: 3000,
      position: 'bottom',
      color: 'danger',
    });
    await toast.present();
  }
}

// Screenshot and email functions
async function openSendToMentorModal() {
  console.log('Opening mentor modal...');
  console.log('Before: showEmailModal =', showEmailModal.value);
  showEmailModal.value = true;
  console.log('After: showEmailModal =', showEmailModal.value);
  
  // Force a re-render
  await nextTick();
  console.log('After nextTick: showEmailModal =', showEmailModal.value);
}

async function closeEmailModal() {
  console.log('Closing email modal...');
  console.log('Before close: showEmailModal =', showEmailModal.value);
  showEmailModal.value = false;
  mentorEmail.value = '';
  console.log('After close: showEmailModal =', showEmailModal.value);
}

async function sendPlanToMentor() {
  if (!mentorEmail.value.trim()) {
    const toast = await toastController.create({
      message: 'Vänligen ange en e-postadress',
      duration: 3000,
      position: 'bottom',
      color: 'warning',
    });
    await toast.present();
    return;
  }

  // Check email limit
  if (emailLimitReached.value) {
    const toast = await toastController.create({
      message: 'Du har redan skickat maximalt antal e-post (3 st) för denna livsstilsplan.',
      duration: 4000,
      position: 'bottom',
      color: 'warning',
    });
    await toast.present();
    return;
  }

  if (exportInProgress.value) return;
  exportInProgress.value = true;
  emailSending.value = true;

  try {
    // Build structured goals data to send
    const goalsData = goals.value.map((goal: any, index: number) => ({
      goal_number: index + 1,
      label: goal.label || '',
      current_value: goal.value || 1,
      target_value: goal.vision || 5,
      target_date: goal.target_date || goal.date || '',
      action_plan: goal.action_plan || '',
      factors: (goal.factors || []).map((f: any) => ({
        category_id: f.category_id,
        label: f.label || '',
        current_value: f.value || 1,
        target_value: f.vision || 5,
      })),
    }));

    // Send email with plan data
    await sendEmailWithPlanData(mentorEmail.value, goalsData);
    
    // Increment email count after successful send
    incrementEmailCount();
    
    // Show success message with remaining count
    const remaining = emailsRemaining.value;
    const toast = await toastController.create({
      message: `Livsstilsplanen har skickats till din mentor! (${remaining} utskick kvar)`,
      duration: 3000,
      position: 'bottom',
      color: 'success',
    });
    await toast.present();
    
    // Close modal
    closeEmailModal();
    
  } catch (error) {
    console.error('❌ Error sending plan to mentor:', error);
    const toast = await toastController.create({
      message: 'Ett fel uppstod. Försök igen.',
      duration: 3000,
      position: 'bottom',
      color: 'danger',
    });
    await toast.present();
  } finally {
    exportInProgress.value = false;
    emailSending.value = false;
  }
}

async function sendEmailWithPlanData(email: string, goalsData: any[]) {
  const payload = {
    mentor_email: email,
    student_id: store.profile?.user_id?.toString() || store.profile?.id?.toString() || '',
    plan_date: new Date().toISOString().split('T')[0],
    goals: goalsData,
  };
  
  // Debug: Log what we're sending
  console.log('📤 Sending plan data to backend:', payload);

  // Send to backend API using useBackendFetch (handles base URL automatically)
  const { data, statusCode } = await useBackendFetch('/send-mentor-email')
    .post(payload)
    .json();

  console.log('📡 Mentor email response:', { data: data.value, statusCode: statusCode.value });
  
  if (statusCode.value && statusCode.value >= 400) {
    throw new Error((data.value as any)?.message || 'Failed to send email');
  }
  
  if (!(data.value as any)?.success) {
    throw new Error((data.value as any)?.message || 'Failed to send email');
  }
  
  return data.value;
}

// Enhanced smooth scroll with better UX
const scrollDown = () => {
  if (contentRef.value) {
    contentRef.value.$el.scrollToBottom(800); // Smoother scroll
  }
};

provide('pageData', {
  number: 4,
  title: 'Min livsstilsplan',
  icon: Globe,
});
provide('colorScheme', 'yellow');
</script>

<template>
  <ion-page>
    <ion-content ref="contentRef" :fullscreen="true" class="ion-padding-bottom">
      <ion-loading :is-open="loading" message="Hämtar din plan..." class="custom-loading" />
      <b-slide-header />

      <!-- Confetti overlay for celebration effect -->
      <div v-if="confettiActive" class="confetti-container">
        <div v-for="i in 50" :key="i" class="confetti" :style="{
          '--size': `${Math.random() * 0.5 + 0.5}rem`,
          '--left': `${Math.random() * 100}%`,
          '--background': `hsl(${Math.random() * 360}, 100%, 50%)`,
          '--rotation': `${Math.random() * 360}deg`,
          '--animation-delay': `${Math.random() * 3}s`,
        }"></div>
      </div>

      <!-- Header with trophy icon -->
      <div class="header-container">
        <div class="header-content">
          <star class="star-icon" />
          <div class="header-text">Sammanfattning</div>
          <star class="star-icon" />
        </div>
        <div class="trophy-badge" :class="{ 'animate-in': animationComplete }">
          <ion-icon :icon="trophy" class="trophy-icon"></ion-icon>
        </div>
      </div>

      <!-- Main content container -->
      <div class="main-content" ref="pageRef">
        <!-- Loading state -->
        <div v-if="loading" class="loading-container">
          <ion-spinner />
          <span class="loading-text">Laddar din livsstilsplan...</span>
        </div>
        
        <!-- Plan content -->
        <div v-else class="plan-content">
          <!-- Success message -->
          <div class="success-message" :class="{ 'animate-in': animationComplete }">
            <ion-icon :icon="checkmarkCircle" class="success-icon"></ion-icon>
            <div class="success-text">Din livsstilsplan är nu klar!</div>
          </div>
          
          <!-- Goals list -->
          <div class="goals-container">
            <h2 class="goals-header">Dina mål</h2>
            <p class="goals-description">Här är en sammanfattning av dina mål och hur du planerar att nå dem:</p>
            
            <ul class="goals-list">
              <b-plan-item 
                v-for="(goal, index) in goals" 
                :key="index" 
                :data="goal"
                class="goal-item"
                :class="{ 'animate-in': animationComplete }"
                :style="{ '--animation-delay': `${index * 0.15 + 0.3}s` }"
              >
                {{ goal.label }}
              </b-plan-item>
            </ul>
          </div>

          <!-- Mascot section -->
          <!-- <div class="mascot-section">
            <div class="speech-container">
              <speech-bubble
                color="yellow"
                class="speech-bubble"
              >
                <div class="speech-content">
                  <span class="speech-title">Äntligen klar!</span>
                  <p class="speech-message">
                    Vi ses igen när det är dags att uppdatera din Livsstilsplan.
                    Lycka till!
                  </p>
                </div>
              </speech-bubble>
            </div>
            
            <img
              class="monster-image"
              :src="monsterUrl"
              alt="Monster"
              :class="{ 'animate-in': animationComplete }"
            />
          </div> -->

             <div
          class="flex justify-center flex-col-reverse md:flex-row items-center relative w-[60%] md:w-[20%] mx-auto"
        >
          <img class="monster md:mt-0" :src="monsterUrl" />

          <div class="relative flex justify-center items-center">
            <speech-bubble
                   color="yellow"
              class="w-[100%] md:rotate-[70deg] -scale-x-100 -z-5"
            />

            <div
              class="absolute md:pl-5 lg:text-[15px] text-[min(5vw,1.15rem)] text-center w-[80%] left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
            >
            Bra jobbat -
             Vi ses igen när det är dags att uppdatera din Livsstilsplan.
                    Lycka till!
            </div>
          </div>
        </div>
        </div>
      </div>

      <!-- Scroll button -->
      <ion-fab
        vertical="bottom"
        horizontal="end"
        slot="fixed"
        v-show="scrollButtonVisible"
        class="scroll-button"
      >
        <ion-fab-button @click="scrollDown">
          <ion-icon :icon="arrowDown" />
        </ion-fab-button>
      </ion-fab>

      <!-- Bottom navigation -->
      <div class="bottom-navigation">
        <div class="button-container">
          <button
            @click="() => { console.log('Button clicked!'); openSendToMentorModal(); }"
            class="mentor-button"
            type="button"
          >
            Skicka till mentor
          </button>
          <b-button
            @click="openOneLastThingModal"
            class="finish-button"
          >
            Avsluta denna livsstilsplan
          </b-button>
        </div>
      </div>
    </ion-content>
  </ion-page>

  <!-- Simple Modal Overlay -->
  <div v-if="showEmailModal" class="modal-overlay" @click="closeEmailModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h2>Skicka till mentor</h2>
        <button @click="closeEmailModal" class="close-btn">×</button>
      </div>
      
      <div class="modal-body">
        <!-- Email count indicator -->
        <div class="email-count-info" :class="{ 'limit-reached': emailLimitReached }">
          <span v-if="emailLimitReached">
            ⚠️ Du har redan skickat 3 e-post för denna livsstilsplan.
          </span>
          <span v-else>
            📧 {{ emailsRemaining }} utskick kvar av {{ MAX_EMAILS_PER_PLAN }}
          </span>
        </div>
                
        <div class="email-form">
          <label>Mentors e-postadress</label>
          <input
            v-model="mentorEmail"
            type="email"
            placeholder="mentor@skola.se"
            :disabled="emailSending || emailLimitReached"
          />
        </div>
        
        <div class="modal-buttons">
          <button
            @click="sendPlanToMentor"
            :disabled="!mentorEmail.trim() || emailSending || emailLimitReached"
            class="send-button"
          >
            {{ emailSending ? 'Skickar...' : 'Skicka livsstilsplan' }}
          </button>
          
          <button
            @click="closeEmailModal"
            class="cancel-button"
            :disabled="emailSending"
          >
            Avbryt
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Base styles */
ion-content {
  --background: #FFFBEE;
  --keyboard-offset: auto !important;
}

/* Header styles */
.header-container {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem 0;
  margin-top: -0.75rem;
  background-color: #FFDD7D;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.75rem 0;
}

.star-icon {
  height: 1.5rem;
  width: 1.5rem;
  margin: 0 0.5rem;
}

.header-text {
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.5rem;
  text-transform: uppercase;
  color: #6B3100;
}

.trophy-badge {
  position: absolute;
  right: 1rem;
  top: 0.75rem;
  background-color: #FFB700;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  justify-content: center;
  align-items: center;
  transform: scale(0);
  transition: transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.15);
}

.trophy-badge.animate-in {
  transform: scale(1);
}

.trophy-icon {
  font-size: 1.5rem;
  color: #FFFFFF;
}

/* Main content styles */
.main-content {
  padding: 1rem 1.5rem;
  margin-bottom: 5rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 30vh;
  gap: 1rem;
}

.loading-text {
  color: #8B6508;
  font-weight: 500;
}

.plan-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Success message styles */
.success-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  background-color: #FFF6D9;
  border-radius: 1rem;
  margin: 1rem 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.4s ease, transform 0.4s ease;
}

.success-message.animate-in {
  opacity: 1;
  transform: translateY(0);
}

.success-icon {
  font-size: 3rem;
  color: #49B267;
  margin-bottom: 0.5rem;
}

.success-text {
  font-size: 1.25rem;
  font-weight: 600;
  color: #333;
  text-align: center;
}

/* Goals section styles */
.goals-container {
  margin: 1rem 0;
}

.goals-header {
  font-size: 1.5rem;
  font-weight: 500;
  color: #6B3100;
  margin-bottom: 0.5rem;
}

.goals-description {
  font-size: 1rem;
  color: #666;
  margin-bottom: 1.5rem;
}

.goals-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 0;
  padding: 0;
  list-style: none;
}

.goal-item {
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.5s ease, transform 0.5s ease;
  transition-delay: var(--animation-delay, 0s);
}

.goal-item.animate-in {
  opacity: 1;
  transform: translateY(0);
}

/* Mascot section styles */
.mascot-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 2rem;
  position: relative;
}

.speech-container {
  width: 100%;
  max-width: 400px;
  margin-bottom: 1rem;
}

.speech-bubble {
  width: 100%;
}

.speech-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 1.5rem;
  text-align: center;
}

.speech-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #6B3100;
  margin-bottom: 0.5rem;
}

.speech-message {
  font-size: 1rem;
  line-height: 1.5;
  color: #333;
}

.monster-image {
  width: 120px;
  height: auto;
  margin-top: 1rem;
  opacity: 0;
  transform: translateY(30px) scale(0.8);
  transition: opacity 0.7s ease, transform 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  transition-delay: 0.6s;
}

.monster-image.animate-in {
  opacity: 1;
  transform: translateY(0) scale(1);
}

/* Button styles */
.scroll-button {
  margin-bottom: 5rem;
}

ion-fab-button {
  --background: #FFB700;
  --color: #6B3100;
  --background-activated: #E6A600;
  --background-hover: #FFCC33;
  --box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  --border-radius: 50%;
  --size: 3rem;
}

.bottom-navigation {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background-color: #FFF3D2;
  padding: 1.5rem;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.05);
  z-index: 10;
}

.button-container {
  display: flex;
  flex-direction: row;
  gap: 0.75rem;
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

@media (max-width: 480px) {
  .button-container {
    flex-direction: column;
  }
}

.mentor-button {
  flex: 1;
  padding: 1rem;
  font-weight: 600;
  font-size: 1.1rem;
  transition: transform 0.2s ease;
  background-color: #FFB700;
  color: #6B3100;
  border: none;
  border-radius: 8px;
  cursor: pointer;
}

.mentor-button:hover {
  background-color: #E6A600;
}

.mentor-button:active {
  transform: scale(0.98);
}

.finish-button {
  flex: 1;
  padding: 1rem;
  font-weight: 600;
  font-size: 1.1rem;
  transition: transform 0.2s ease;
}

.finish-button:active {
  transform: scale(0.98);
}

/* Simple Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.modal-content {
  background: #FFFBEE;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.25);
  font-family: 'Roboto', sans-serif;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  background: linear-gradient(135deg, #FFDD7D 0%, #FDC833 100%);
  border-radius: 16px 16px 0 0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.3rem;
  font-weight: 700;
  color: #6B3100;
  letter-spacing: -0.01em;
}

.close-btn {
  background: rgba(255, 255, 255, 0.3);
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.4rem 0.6rem;
  color: #6B3100;
  border-radius: 8px;
  transition: background 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.5);
}

.modal-body {
  padding: 1.5rem;
}

.email-count-info {
  background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%);
  border: 1px solid #81c784;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 500;
  color: #2e7d32;
}

.email-count-info.limit-reached {
  background: linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%);
  border-color: #ff9800;
  color: #e65100;
}

.warning-message {
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  text-align: center;
}

.warning-icon {
  font-size: 2rem;
  color: #f59e0b;
  margin-bottom: 0.5rem;
}

.warning-message h3 {
  margin: 0 0 0.5rem 0;
  color: #92400e;
  font-size: 1.1rem;
}

.warning-message p {
  margin: 0;
  color: #92400e;
  font-size: 0.9rem;
  line-height: 1.4;
}

.email-form {
  margin: 0.5rem 0 1.5rem 0;
}

.email-form label {
  display: block;
  margin-bottom: 0.6rem;
  font-weight: 600;
  font-size: 0.95rem;
  color: #6B3100;
}

.email-form input {
  width: 100%;
  padding: 0.875rem 1rem;
  border: 2px solid #FFDD7D;
  border-radius: 12px;
  font-size: 1rem;
  font-family: 'Roboto', sans-serif;
  background: white;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.email-form input:focus {
  outline: none;
  border-color: #FDC833;
  box-shadow: 0 0 0 4px rgba(253, 200, 51, 0.2);
}

.email-form input::placeholder {
  color: #ab8008;
  opacity: 0.6;
}

.modal-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
}

.send-button {
  background: linear-gradient(135deg, #FDC833 0%, #f5cc59 100%);
  color: #6B3100;
  border: none;
  padding: 1rem 1.5rem;
  border-radius: 30px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  box-shadow: 0 4px 12px rgba(253, 200, 51, 0.3);
}

.send-button:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(253, 200, 51, 0.4);
}

.send-button:active:not(:disabled) {
  transform: translateY(0);
}

.send-button:disabled {
  background: #e5e7eb;
  color: #9ca3af;
  cursor: not-allowed;
  box-shadow: none;
}

.cancel-button {
  background: transparent;
  color: #6B3100;
  border: 2px solid #FFDD7D;
  padding: 0.875rem 1.5rem;
  border-radius: 30px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.cancel-button:hover:not(:disabled) {
  background: rgba(255, 221, 125, 0.2);
}

.cancel-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Confetti animation */
.confetti-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 100;
  overflow: hidden;
}

.confetti {
  position: absolute;
  width: var(--size);
  height: var(--size);
  background: var(--background);
  top: -10vh;
  left: var(--left);
  opacity: 0;
  transform: rotate(var(--rotation));
  animation: confetti-fall 6s ease-in-out forwards;
  animation-delay: var(--animation-delay);
}

@keyframes confetti-fall {
  0% {
    opacity: 1;
    top: -10vh;
    transform: translateX(0) rotate(var(--rotation));
  }
  25% {
    opacity: 1;
    transform: translateX(20px) rotate(calc(var(--rotation) + 45deg));
  }
  50% {
    opacity: 1;
    transform: translateX(-20px) rotate(calc(var(--rotation) + 90deg));
  }
  75% {
    opacity: 1;
    transform: translateX(20px) rotate(calc(var(--rotation) + 135deg));
  }
  100% {
    opacity: 0;
    top: 110vh;
    transform: translateX(-20px) rotate(calc(var(--rotation) + 180deg));
  }
}

/* Responsive styles */
@media (min-width: 768px) {
  .header-container {
    margin-top: 0;
  }
  
  .main-content {
    max-width: 800px;
    margin: 0 auto 6rem auto;
    padding: 2rem;
  }
  
  .mascot-section {
    flex-direction: row;
    justify-content: center;
    align-items: flex-start;
    gap: 2rem;
  }
  
  .speech-container {
    margin-bottom: 0;
  }
  
  .monster-image {
    width: 150px;
    margin-top: 3rem;
  }
  
  .bottom-navigation {
    padding: 1.5rem 3rem;
  }
}

@media (min-width: 1024px) {
  .main-content {
    max-width: 900px;
  }
  
  .goals-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  .monster-image {
    width: 180px;
  }
}
</style>